import React, { useState } from "react";
import {
  Button,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  Box,
} from "@mui/material";
import { Delete } from "@mui/icons-material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import "dayjs/locale/it";
import { useTranslation } from "react-i18next";
import {
  Divider,
  MainContainer,
  SectionTitle,
  SingleSlotContainer,
  SingleSlotContent,
  SlotsGridItem,
} from "@components/TimeSlotCst/style";
import DownloaderUploaderCst from "@components/DownloaderUploaderCst";
import { ServicesURL } from "@utils/utilsApi";
import useSWRMutation from "swr/mutation";
import { MethodType } from "src/services/type";
import { fetcher } from "src/services/config";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import dayjs from "dayjs";

interface MultiUploadCstProps {
  initialFiles: any[];
  onChange: (newFile: any[]) => void;
  error: boolean;
  errorMessage: string;
  maxFiles?: number;
  rows: number;
  translationKey: string;
  column: any;
  onDownload: (e: any, fileName: any) => void;
  mutate?: (prev: any) => any;
}

const MultiUploadCst = ({
  initialFiles,
  onChange,
  error,
  errorMessage,
  column,
  onDownload,
  mutate,
}: MultiUploadCstProps) => {
  const { t } = useTranslation(["projects-context-page"]);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [fileSlots, setFileSlots] = useState<any[]>(initialFiles || []);
  const [fileSlotData, setFileSlotData] = useState<any>({ original_name: "" });
  const [idFileDeleted, setIdFileDeleted] = useState<
    { fileId: any; index: any } | undefined
  >();
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
    description?: string;
  }>();

  const addFile = (e: any) => {
    const updatedFileList = [
      ...fileSlots,
      {
        ...fileSlotData,
        file: e,
        id: null,
        original_name: e?.name,
        index: fileSlots.length,
      },
    ];

    setFileSlotData({
      id: null,
      original_name: "",
    });

    setFileSlots(updatedFileList);
    onChange(updatedFileList);
  };

  const { trigger: triggerDeleteFile } = useSWRMutation(
    `${ServicesURL.deleteUserFile.replace(":userUploadedFileId", idFileDeleted && idFileDeleted.fileId)}`,
    (url: string) => {
      return fetcher<any>(url, MethodType.DELETE);
    },
  );

  const handleDelete = React.useCallback(async () => {
    if (idFileDeleted && idFileDeleted.fileId) {
      setOpenToast({ s: StatusEnum.LOADING, message: "loading" });
      try {
        await triggerDeleteFile();
        const updatedFileList = [...fileSlots];
        updatedFileList.splice(
          fileSlots.findIndex((file) => file.id === idFileDeleted.fileId),
          1,
        );
        setFileSlots(updatedFileList);
        setIdFileDeleted(undefined);
        mutate && mutate((prev: any) => (prev ?? 0) + 1);
        setOpenToast({ s: StatusEnum.OK, message: "" });
      } catch (error) {
        setIdFileDeleted(undefined);
        setOpenToast(undefined);
        console.error(error);
      }
    } else if (idFileDeleted && idFileDeleted.index != null) {
      const updatedFileList = [...fileSlots];
      updatedFileList.splice(
        fileSlots.findIndex((file) => file.index === idFileDeleted.index),
        1,
      );
      setFileSlots(updatedFileList);
      setIdFileDeleted(undefined);
      setOpenModalDelete(!openModalDelete);
      onChange(updatedFileList);
    }
  }, [idFileDeleted, triggerDeleteFile, fileSlots, setFileSlots, onChange]);

  return (
    <Grid item xs={12}>
      <MainContainer theme={theme} sx={{ mt: "0 !important" }}>
        <SectionTitle theme={theme}>{t("multiupload-title")}</SectionTitle>
        <Grid container spacing={"24px"}>
          <Grid item xs={12}>
            <DownloaderUploaderCst
              disabled={column.disabled}
              name={column.header}
              accept={column?.accept}
              maxSize={column?.maxSize}
              required={column?.required}
              url={column.service}
              fileName={column.fileName}
              downloadDisabled={column.downloadDisabled}
              onChange={(e: any) => {
                addFile(e);
              }}
              textTooltip={column?.tooltip}
              tooltipFile={column?.tooltipFile}
              tooltipFileIta={column?.tooltipFileIta}
              tooltipFileEn={column?.tooltipFileEn}
            />
            {error && (
              <Typography
                color={theme.palette.error.main}
                sx={{ fontSize: "0.75rem" }}
              >
                {error ? (errorMessage ? errorMessage : null) : null}
              </Typography>
            )}
          </Grid>

          {fileSlots && fileSlots.length > 0 && (
            <Grid item xs={12}>
              <SlotsGridItem gap={2}>
                <Divider theme={theme} />

                {fileSlots.map((range, rangeIndex) => {
                  return (
                    <SingleSlotContainer
                      theme={theme}
                      key={`range_${rangeIndex}_${range.original_name}`}
                    >
                      {!isSmallScreen && (
                        <>
                          <IconButton
                            color={"primary"}
                            onClick={() => {
                              setIdFileDeleted({
                                fileId: range.id,
                                index: rangeIndex,
                              });
                              setOpenModalDelete(true);
                            }}
                          >
                            <Delete />
                          </IconButton>
                          <IconButton
                            disabled={!range.id}
                            onClick={() =>
                              range.id &&
                              onDownload(
                                `${ServicesURL.getUserFile.replace(":userUploadedFileId", range.id)}`,
                                range.original_name,
                              )
                            }
                          >
                            <FileDownloadIcon />
                          </IconButton>
                        </>
                      )}
                      <SingleSlotContent
                        gap={isSmallScreen ? "10px" : "5px"}
                        theme={theme}
                      >
                        <Typography variant="body1" fontWeight="bold">
                          {range.original_name}
                          <span style={{ fontWeight: "normal", color: "#888" }}>
                            {" "}
                            - {dayjs(range.date).format("DD/MM/YYYY HH:mm:ss")}
                          </span>
                        </Typography>

                        {isSmallScreen && (
                          <>
                            <Button
                              onClick={() => {
                                setIdFileDeleted(range.id);
                                setOpenModalDelete(true);
                              }}
                              startIcon={<Delete />}
                              color={"primary"}
                              variant={"contained"}
                            >
                              {t("multiupload-delete-slot-btn")}
                            </Button>
                            <Button
                              onClick={() =>
                                range.link &&
                                onDownload(
                                  `${ServicesURL.getUserFile.replace(":userUploadedFileId", range.id)}`,
                                  range.original_name,
                                )
                              }
                              startIcon={<FileDownloadIcon />}
                              color={"primary"}
                              variant={"contained"}
                            >
                              {t("multiupload-download-slot-btn")}
                            </Button>
                          </>
                        )}
                      </SingleSlotContent>
                    </SingleSlotContainer>
                  );
                })}
              </SlotsGridItem>
            </Grid>
          )}
        </Grid>
        <ModalConfirmsCst
          open={!!openModalDelete}
          title={t("delete-item", { ns: "modal" })}
          description={t("confirm-delete", { ns: "modal" })}
          routeToSamePage={true}
          onCallBackClosed={() => {
            setOpenModalDelete(!openModalDelete);
            setOpenToast(undefined);
          }}
          onActionConfirmed={() => handleDelete()}
          status={openToast}
        />
      </MainContainer>
    </Grid>
  );
};

export default MultiUploadCst;
